import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useLanguage } from '../hooks/useLanguage';
import { useTranslation } from 'react-i18next';
import {
  ChefHat, Coffee, Truck, Monitor, UtensilsCrossed, QrCode,
  Calculator, Brain, ShoppingCart, Zap, Shield, Globe,
  ArrowLeft, Sparkles, Star, ChevronDown, Play, Menu, X,
  Check, BarChart3, FileText, Headphones, Clock,
  Receipt, TrendingUp, Mic, Wand2, MessageCircle, Users,
  Sun, Moon
} from 'lucide-react';
import ChatWidget from '../components/ChatWidget';
import SEOHead from '../components/SEOHead';
import { buildFAQSchema, buildBreadcrumb, SITE } from '../config/seo';

gsap.registerPlugin(ScrollTrigger);

const L = (ar, en, lang) => lang === 'ar' ? ar : en;

const TYPES = [
  {
    id: 'restaurant', icon: UtensilsCrossed, color: '#D97706', bg: 'rgba(217,119,6,0.08)', border: 'rgba(217,119,6,0.3)',
    ar: { name: 'مطعم', desc: 'مطاعم، بيتزا، وجبات سريعة، مطابخ سحابية' },
    en: { name: 'Restaurant', desc: 'Restaurants, Pizza, Fast Food, Cloud Kitchens' },
  },
  {
    id: 'cafe', icon: Coffee, color: '#0D9488', bg: 'rgba(13,148,136,0.08)', border: 'rgba(13,148,136,0.3)',
    ar: { name: 'كافيه', desc: 'كوفي شوب، مخابز، كافتيريا' },
    en: { name: 'Café', desc: 'Coffee Shops, Bakeries, Cafeterias' },
  },
  {
    id: 'food_truck', icon: Truck, color: '#7C3AED', bg: 'rgba(124,58,237,0.08)', border: 'rgba(124,58,237,0.3)',
    ar: { name: 'عربة متنقلة', desc: 'عربات طعام، شاي، عصير' },
    en: { name: 'Food Truck', desc: 'Food, Tea & Juice Trucks' },
  },
];

const FEATURES = {
  restaurant: [
    { icon: Monitor, color: '#10B981', ar: { t: 'نقطة بيع + إدارة طاولات', d: 'نظام كاشير متقدم مع خريطة طاولات تفاعلية وفتح/إغلاق ورديات وطرق دفع متعددة' }, en: { t: 'POS + Table Management', d: 'Advanced cashier system with interactive table map, shift management, and multiple payment methods' } },
    { icon: ChefHat, color: '#F59E0B', ar: { t: 'شاشة مطبخ ذكية (KDS)', d: 'تذاكر طلبات فورية مع تحديث حالة مباشر وإشعارات صوتية وطباعة تلقائية' }, en: { t: 'Smart Kitchen Display', d: 'Real-time order tickets with live status updates, audio notifications, and auto-printing' } },
    { icon: Truck, color: '#6366F1', ar: { t: 'إدارة أسطول التوصيل', d: 'تتبع السائقين على الخريطة، توزيع الطلبات تلقائياً، وبوابة سائق مستقلة' }, en: { t: 'Delivery Fleet Management', d: 'Track drivers on map, auto-assign orders, and independent driver portal' } },
    { icon: FileText, color: '#EC4899', ar: { t: 'فوترة إلكترونية ZATCA', d: 'فواتير معتمدة مع QR كود وتقارير ضريبية جاهزة ومتوافقة 100% مع هيئة الزكاة' }, en: { t: 'ZATCA E-Invoicing', d: 'Certified invoices with QR code, ready tax reports, 100% ZATCA compliant' } },
    { icon: MessageCircle, color: '#D97706', ar: { t: 'ZouqBot - وكيل طلبات ذكي', d: 'بوت يفهم العامية السعودية، يأخذ الطلبات، يقترح الأطباق، ويتعامل مع عملاؤك بطلاقة' }, en: { t: 'ZouqBot - AI Order Agent', d: 'A bot that understands Saudi dialect, takes orders, suggests dishes, and chats naturally' } },
    { icon: Calculator, color: '#3B82F6', ar: { t: 'محاسبة شاملة وتقارير', d: 'لوحة مالية، مصروفات، أرباح وخسائر، تحليل تكلفة طعام، وتنبيهات ذكية' }, en: { t: 'Full Accounting & Reports', d: 'Financial dashboard, expenses, P&L, food cost analysis, and smart alerts' } },
  ],
  cafe: [
    { icon: Zap, color: '#10B981', ar: { t: 'نقطة بيع سريعة', d: 'واجهة مصممة للخدمة السريعة - أضف المنتجات بنقرة واحدة مع حساب فوري' }, en: { t: 'Fast POS System', d: 'Interface designed for quick service - add products in one tap with instant calculation' } },
    { icon: Clock, color: '#F59E0B', ar: { t: 'شاشة محطة التحضير', d: 'عرض الطلبات للباريستا مع توقيت تحضير وتحديث حالة مباشر' }, en: { t: 'Preparation Station Display', d: 'Show orders to barista with prep timing and live status updates' } },
    { icon: QrCode, color: '#6366F1', ar: { t: 'طلب QR من الطاولة', d: 'عملاؤك يطلبون من جوالهم بمسح QR كود الطاولة - بدون انتظار' }, en: { t: 'QR Table Ordering', d: 'Customers order from their phone by scanning the table QR code - no waiting' } },
    { icon: Globe, color: '#EC4899', ar: { t: 'صفحة طلبات تحادثية', d: 'موقع تفاعلي لمشروعك يقبل الطلبات عبر محادثة ذكية مع ZouqBot' }, en: { t: 'Conversational Order Page', d: 'Interactive website that accepts orders via smart conversation with ZouqBot' } },
    { icon: MessageCircle, color: '#0D9488', ar: { t: 'ZouqBot - مساعد ذكي', d: 'يرد على استفسارات العملاء، يقترح المشروبات، ويأخذ الطلبات بالعامية' }, en: { t: 'ZouqBot - Smart Assistant', d: 'Answers customer questions, suggests drinks, and takes orders in dialect' } },
    { icon: TrendingUp, color: '#3B82F6', ar: { t: 'تحليل التكاليف والأرباح', d: 'تتبع تكلفة كل منتج، هوامش الربح، والمصروفات اليومية مع تقارير مفصلة' }, en: { t: 'Cost & Profit Analysis', d: 'Track cost per product, profit margins, and daily expenses with detailed reports' } },
  ],
  food_truck: [
    { icon: Monitor, color: '#10B981', ar: { t: 'نقطة بيع محمول', d: 'يعمل من أي جهاز - جوال، تابلت، أو لابتوب. مصمم للعمل في أي مكان' }, en: { t: 'Mobile POS', d: 'Works from any device - phone, tablet, or laptop. Designed to work anywhere' } },
    { icon: UtensilsCrossed, color: '#F59E0B', ar: { t: 'إدارة قائمة بسيطة', d: 'أضف وعدّل منتجاتك بسهولة مع دعم الصور والأسعار والتصنيفات' }, en: { t: 'Simple Menu Management', d: 'Add and edit products easily with image, price, and category support' } },
    { icon: Receipt, color: '#7C3AED', ar: { t: 'فوترة إلكترونية', d: 'فواتير إلكترونية معتمدة مع QR كود - متوافقة مع متطلبات زاتكا' }, en: { t: 'Electronic Invoicing', d: 'Certified e-invoices with QR code - compliant with ZATCA requirements' } },
    { icon: BarChart3, color: '#EC4899', ar: { t: 'تتبع المبيعات اليومية', d: 'شاهد إيراداتك لحظة بلحظة مع ملخص يومي وأسبوعي وشهري' }, en: { t: 'Daily Sales Tracking', d: 'See your revenue in real-time with daily, weekly, and monthly summaries' } },
    { icon: MessageCircle, color: '#D97706', ar: { t: 'طلبات سريعة عبر ZouqBot', d: 'عملاؤك يطلبون من جوالهم عبر محادثة ذكية - مثالي لطوابير العربات' }, en: { t: 'Quick ZouqBot Orders', d: 'Customers order from their phone via smart chat - perfect for truck queues' } },
    { icon: Calculator, color: '#3B82F6', ar: { t: 'إدارة المصروفات', d: 'سجّل مصروفاتك اليومية (بضاعة، وقود، إيجار) وشاهد صافي ربحك الحقيقي' }, en: { t: 'Expense Management', d: 'Log daily expenses (supplies, fuel, rent) and see your real net profit' } },
  ],
};

const WHY_US = [
  { icon: Brain, color: '#6366F1', ar: { t: 'ذكاء اصطناعي متقدم', d: 'ZouqBot يفهم العامية السعودية ويتعلم من كل محادثة. يأخذ الطلبات، يقترح الأطباق، ويحل مشاكل العملاء تلقائياً.' }, en: { t: 'Advanced AI', d: 'ZouqBot understands Saudi dialect and learns from every conversation. Takes orders, suggests dishes, and resolves issues automatically.' } },
  { icon: Shield, color: '#10B981', ar: { t: 'متوافق 100% مع زاتكا', d: 'فوترة إلكترونية معتمدة، QR كود على كل فاتورة، تقارير ضريبية جاهزة. لا تقلق من المخالفات.' }, en: { t: '100% ZATCA Compliant', d: 'Certified e-invoicing, QR code on every invoice, ready tax reports. Never worry about violations.' } },
  { icon: Globe, color: '#D97706', ar: { t: 'عربي بالكامل', d: 'من الواجهة للتقارير للمحاسبة - كل شيء بالعربي الفصيح والعامية. دعم RTL كامل.' }, en: { t: 'Fully Arabic', d: 'From interface to reports to accounting - everything in Arabic. Full RTL support.' } },
  { icon: Zap, color: '#EC4899', ar: { t: 'جاهز من اليوم الأول', d: 'سجّل، اختر نوع مشروعك، وابدأ العمل في دقائق. بيانات تجريبية جاهزة وإعداد سهل بدون تعقيد.' }, en: { t: 'Ready from Day One', d: 'Sign up, choose your business type, and start in minutes. Demo data ready, easy setup, no complexity.' } },
];

const SEO_SECTIONS = [
  {
    id: 'restaurant', icon: UtensilsCrossed, color: '#D97706',
    ar: { title: 'نظام إدارة مطاعم ذكي - الحل الشامل', body: 'ذوق AI يقدم لك نظام إدارة مطاعم متكامل مصمم للسوق السعودي. من نقطة البيع المتقدمة مع إدارة الطاولات، إلى شاشة المطبخ الذكية التي تعرض الطلبات فورياً، وأسطول التوصيل مع تتبع السائقين - كل ما يحتاجه مطعمك في منصة واحدة. نظام المحاسبة الشامل يتتبع مصروفاتك وأرباحك مع تقارير تكلفة الطعام وتنبيهات ذكية. والفوترة الإلكترونية المتوافقة مع زاتكا تضمن التزامك الكامل.', cta: 'جرب نظام المطاعم الآن' },
    en: { title: 'Smart Restaurant Management System - Complete Solution', body: 'Zouq AI provides a comprehensive restaurant management system designed for the Saudi market. From advanced POS with table management to smart kitchen displays showing orders instantly, and delivery fleet with driver tracking - everything your restaurant needs in one platform. The full accounting system tracks expenses and profits with food cost reports and smart alerts. ZATCA-compliant e-invoicing ensures full compliance.', cta: 'Try Restaurant System Now' },
    keywords: ['نظام إدارة مطاعم', 'برنامج كاشير مطعم', 'شاشة مطبخ', 'نقطة بيع مطعم', 'فوترة مطاعم زاتكا'],
  },
  {
    id: 'cafe', icon: Coffee, color: '#0D9488',
    ar: { title: 'برنامج إدارة كافيهات متكامل', body: 'صُمم ذوق AI ليناسب إيقاع عملك السريع في الكافيه. نقطة البيع السريعة تتيح لك خدمة العملاء بنقرة واحدة، وشاشة محطة التحضير تعرض الطلبات للباريستا فوراً. عملاؤك يطلبون من جوالهم عبر QR كود الطاولة أو عبر ZouqBot على صفحتك التحادثية. تابع تكلفة كل مشروب وهوامش الربح مع تقارير مالية مفصلة تساعدك على اتخاذ قرارات أذكى.', cta: 'جرب نظام الكافيهات الآن' },
    en: { title: 'Complete Café Management Software', body: 'Zouq AI is designed for your café\'s fast pace. The quick POS lets you serve customers in one tap, and the preparation station display shows orders to the barista instantly. Customers order from their phone via table QR code or ZouqBot on your conversational page. Track cost per drink and profit margins with detailed financial reports that help you make smarter decisions.', cta: 'Try Café System Now' },
    keywords: ['برنامج إدارة كافيه', 'كاشير كوفي شوب', 'نظام طلبات كافيه', 'إدارة مقهى'],
  },
  {
    id: 'food_truck', icon: Truck, color: '#7C3AED',
    ar: { title: 'نظام نقاط بيع للعربات المتنقلة', body: 'مصمم خصيصاً لعربات الطعام والشاي والعصير. ذوق AI يعمل من أي جهاز - جوالك، تابلت، أو لابتوب - بدون أجهزة مكلفة. أدر قائمتك بسهولة، أصدر فواتير إلكترونية متوافقة مع زاتكا، وتابع مبيعاتك اليومية لحظة بلحظة. سجّل مصروفاتك (بضاعة، وقود، إيجار موقع) وشاهد صافي ربحك الحقيقي. عملاؤك يطلبون من جوالهم عبر ZouqBot - مثالي لتقليل طوابير الانتظار.', cta: 'جرب نظام العربات الآن' },
    en: { title: 'POS System for Food Trucks', body: 'Designed specifically for food, tea, and juice trucks. Zouq AI works from any device - phone, tablet, or laptop - no expensive hardware needed. Manage your menu easily, issue ZATCA-compliant e-invoices, and track daily sales in real-time. Log expenses (supplies, fuel, location rent) and see your real net profit. Customers order via ZouqBot - perfect for reducing queue lines.', cta: 'Try Food Truck System Now' },
    keywords: ['نقطة بيع عربة طعام', 'كاشير عربة', 'نظام عربة شاي', 'فوترة عربات متنقلة'],
  },
];

const TESTIMONIALS = [
  { ar: { name: 'الشيف أحمد', role: 'مطعم الأصيل - الرياض', text: 'ذوق AI غيّرت طريقة إدارتنا بالكامل. شاشة المطبخ وحدها وفّرت علينا 30% من وقت التحضير، والفوترة الإلكترونية خلصتنا من صداع زاتكا.' }, en: { name: 'Chef Ahmed', role: 'Al Aseel Restaurant - Riyadh', text: 'Zouq AI completely transformed how we operate. The kitchen display alone saved us 30% prep time, and e-invoicing solved our ZATCA headaches.' } },
  { ar: { name: 'سارة المطيري', role: 'كافيه لمسة - جدة', text: 'عملاؤنا يحبون طلب القهوة من جوالهم عبر QR الطاولة. ZouqBot يأخذ الطلبات بطلاقة وكأنه باريستا محترف!' }, en: { name: 'Sarah Al-Mutairi', role: 'Lamsa Café - Jeddah', text: 'Our customers love ordering coffee from their phone via table QR. ZouqBot takes orders fluently as if it were a professional barista!' } },
  { ar: { name: 'خالد العنزي', role: 'عربة شاي الديرة - الدمام', text: 'قبل ذوق AI كنت أحسب كل شيء يدوياً. الحين أعرف ربحي اليومي بالضبط والفواتير الإلكترونية جاهزة تلقائياً.' }, en: { name: 'Khaled Al-Anazi', role: 'Deera Tea Truck - Dammam', text: 'Before Zouq AI I calculated everything manually. Now I know my exact daily profit and e-invoices are generated automatically.' } },
];

const FAQS = [
  { ar: { q: 'ما هي ذوق AI؟', a: 'ذوق AI منصة ذكاء اصطناعي شاملة لإدارة المشاريع الغذائية في السعودية. تدعم المطاعم، الكافيهات، والعربات المتنقلة. تتضمن نقطة بيع، شاشة مطبخ، فوترة إلكترونية ZATCA، محاسبة شاملة، وZouqBot - وكيل طلبات ذكي يفهم العامية السعودية.' }, en: { q: 'What is Zouq AI?', a: 'Zouq AI is a comprehensive AI platform for managing food businesses in Saudi Arabia. It supports restaurants, cafés, and food trucks. It includes POS, kitchen display, ZATCA e-invoicing, full accounting, and ZouqBot - a smart ordering agent that understands Saudi dialect.' } },
  { ar: { q: 'هل تدعم جميع أنواع المشاريع الغذائية؟', a: 'نعم! ذوق AI تدعم 46 نوع مشروع غذائي: مطاعم (شاورما، برجر، بروستد، بيتزا)، مطابخ عربية وعالمية (سوري، مصري، سوداني، تركي)، كافيهات ومشروبات، حلويات (كنافة، دونات، كيك، شوكولاتة)، وخدمات حديثة (فود ترك، تموين، بوفيه مفتوح). النظام يتكيف تلقائياً حسب نوع مشروعك.' }, en: { q: 'Does it support all food business types?', a: 'Yes! Zouq AI supports 46 food business types: restaurants (shawarma, burger, broasted, pizza), Arabic and international cuisines (Syrian, Egyptian, Sudanese, Turkish), cafés and beverages, desserts (kunafa, donuts, cakes, chocolate), and modern services (food trucks, catering, buffet). The system adapts automatically based on your business type.' } },
  { ar: { q: 'هل النظام يدعم العربية بالكامل؟', a: 'بالتأكيد! كل شيء بالعربي - من واجهة المستخدم إلى التقارير المالية. ZouqBot يتحدث العامية السعودية بطلاقة ويتعامل مع عملاؤك بشكل طبيعي.' }, en: { q: 'Is the system fully Arabic?', a: 'Absolutely! Everything is in Arabic - from the user interface to financial reports. ZouqBot speaks Saudi dialect fluently and interacts with your customers naturally.' } },
  { ar: { q: 'هل الفوترة متوافقة مع زاتكا؟', a: 'نعم، 100%. كل فاتورة تصدر مع QR كود معتمد وبيانات ضريبية كاملة. التقارير الضريبية جاهزة للتقديم مباشرة. لا حاجة لأنظمة فوترة إضافية.' }, en: { q: 'Is invoicing ZATCA compliant?', a: 'Yes, 100%. Every invoice is issued with certified QR code and complete tax data. Tax reports are ready for direct submission. No need for additional invoicing systems.' } },
  { ar: { q: 'كيف أبدأ؟', a: 'سهل جداً! اختر نوع مشروعك من الأعلى واضغط "جرب مجاناً". ستحصل على حساب تجريبي مجاني لمدة 14 يوم مع بيانات جاهزة تناسب نوع مشروعك. بدون بطاقة ائتمان.' }, en: { q: 'How do I start?', a: 'Very easy! Choose your business type above and click "Try Free". You\'ll get a free 14-day trial with ready data that fits your business type. No credit card required.' } },
];

const PLANS = [
  { id: 'trial', price: 0, popular: false, ar: { name: 'تجربة مجانية', period: '14 يوم', features: ['نقطة بيع كاملة', 'ZouqBot AI', 'فوترة إلكترونية', '1 فرع'] }, en: { name: 'Free Trial', period: '14 days', features: ['Full POS', 'ZouqBot AI', 'E-Invoicing', '1 Branch'] } },
  { id: 'basic', price: 99, popular: false, ar: { name: 'أساسي', period: 'شهرياً', features: ['كل مميزات التجربة', 'محاسبة أساسية', 'تقارير يومية', '2 فرع'] }, en: { name: 'Basic', period: '/month', features: ['All trial features', 'Basic accounting', 'Daily reports', '2 Branches'] } },
  { id: 'pro', price: 249, popular: true, ar: { name: 'احترافي', period: 'شهرياً', features: ['كل مميزات الأساسي', 'محاسبة شاملة + تنبيهات', 'أسطول توصيل', '5 فروع'] }, en: { name: 'Pro', period: '/month', features: ['All basic features', 'Full accounting + alerts', 'Delivery fleet', '5 Branches'] } },
  { id: 'enterprise', price: 499, popular: false, ar: { name: 'مؤسسات', period: 'شهرياً', features: ['كل مميزات الاحترافي', 'API مخصص', 'دعم مخصص 24/7', 'فروع غير محدودة'] }, en: { name: 'Enterprise', period: '/month', features: ['All pro features', 'Custom API', '24/7 dedicated support', 'Unlimited branches'] } },
];

const SOCIAL_PROOF_MESSAGES = {
  ar: [
    { name: 'أحمد', city: 'الرياض', action: 'بدأ تجربته', type: 'شاورما' },
    { name: 'سارة', city: 'جدة', action: 'أنشأت مشروعها', type: 'كافيه مختص' },
    { name: 'خالد', city: 'الدمام', action: 'فعّل نظامه', type: 'كبسة ومندي' },
    { name: 'نورة', city: 'مكة', action: 'بدأت تجربتها', type: 'كنافة وحلويات شرقية' },
    { name: 'فهد', city: 'الرياض', action: 'أطلق مشروعه', type: 'بروستد' },
    { name: 'مريم', city: 'جدة', action: 'سجّلت مشروعها', type: 'مطبخ حجازي' },
    { name: 'عبدالله', city: 'الرياض', action: 'أنشأ مشروعه', type: 'فود ترك' },
    { name: 'لمى', city: 'جدة', action: 'بدأت تجربتها', type: 'دونات' },
    { name: 'محمد', city: 'الدمام', action: 'فعّل نظامه', type: 'مطبخ سوري' },
    { name: 'هند', city: 'الرياض', action: 'أطلقت مشروعها', type: 'كيك وتورتات' },
  ],
  en: [
    { name: 'Ahmed', city: 'Riyadh', action: 'started their trial', type: 'Shawarma' },
    { name: 'Sara', city: 'Jeddah', action: 'created their business', type: 'Specialty Café' },
    { name: 'Khalid', city: 'Dammam', action: 'activated their system', type: 'Kabsa & Mandi' },
    { name: 'Noura', city: 'Makkah', action: 'started their trial', type: 'Kunafa & Eastern Sweets' },
    { name: 'Fahad', city: 'Riyadh', action: 'launched their business', type: 'Broasted Chicken' },
    { name: 'Mariam', city: 'Jeddah', action: 'registered their business', type: 'Hejazi Kitchen' },
    { name: 'Abdullah', city: 'Riyadh', action: 'created their business', type: 'Food Truck' },
    { name: 'Lama', city: 'Jeddah', action: 'started their trial', type: 'Donuts' },
    { name: 'Mohammed', city: 'Dammam', action: 'activated their system', type: 'Syrian Cuisine' },
    { name: 'Hind', city: 'Riyadh', action: 'launched their business', type: 'Cake Shop' },
  ],
};

const MOCK_MENUS = {
  _default: ['طبق رئيسي|45', 'طبق ثاني|35', 'مشروب|15', 'حلى|25'],
  shawarma: ['شاورما لحم|25', 'شاورما دجاج|20', 'فروج|55', 'عصير|8'],
  burger: ['سمش برجر|32', 'تشيز برجر|28', 'بطاطس|12', 'كولا|8'],
  pizza: ['مارغريتا|35', 'بيبروني|42', 'خضار|30', 'بريد|12'],
  fried_chicken: ['4 قطع|35', 'ستربس|28', 'ناقتس|20', 'سلو|8'],
  sandwiches: ['كلوب|25', 'تونة|18', 'دجاج|22', 'عصير|10'],
  kabsa_mandi: ['كبسة لحم|45', 'مندي|38', 'مضغوط|42', 'سلطة|8'],
  saudi_grills: ['مشكل|85', 'كباب|45', 'ريش|65', 'حمص|12'],
  foul_tamees: ['فول|12', 'تميس|5', 'فلافل|10', 'شاي|3'],
  hejazi_food: ['سليق|40', 'مطبق|25', 'ميعار|35', 'شربة|15'],
  traditional_saudi: ['جريش|35', 'مرقوق|30', 'قرصان|28', 'كليجا|15'],
  yemeni_food: ['مندي|42', 'فحسة|38', 'سلتة|35', 'بنت الصحن|25'],
  specialty_coffee: ['V60|25', 'لاتيه|22', 'كابتشينو|20', 'كروسان|15'],
  fresh_juices: ['برتقال|15', 'موز|18', 'فراولة|16', 'أفوكادو|20'],
  arabic_bakery: ['كنافة|20', 'بسبوسة|15', 'كيك|25', 'معمول|18'],
  bubble_tea: ['ميلك تي|22', 'تارو|24', 'ماتشا|25', 'فروت تي|20'],
  seafood: ['سمك|65', 'ربيان|55', 'سيباس|85', 'شوربة|25'],
  sushi: ['سالمون|45', 'كاليفورنيا|35', 'نيغيري|55', 'إيدامامي|18'],
  italian: ['باستا|38', 'ريزوتو|42', 'بروشيتا|22', 'تيراميسو|25'],
  indian: ['بيرياني|35', 'تكا مسالا|38', 'نان|8', 'سمبوسة|12'],
  cloud_kitchen: ['وجبة 1|30', 'وجبة 2|35', 'وجبة 3|28', 'إضافة|10'],
  broasted: ['2 قطعة|12', '4 قطع وجبة|28', 'ستربس|25', 'ثوم|2'],
  food_truck: ['سماش برجر|15', 'تاكو|10', 'ناتشوز|18', 'ليمونادة|8'],
  falafel_hummus: ['فلافل|5', 'حمص|8', 'فول|7', 'شكشوكة|15'],
  pastries_fatayer: ['سمبوسة 10|8', 'فطائر|4', 'منقوشة|5', 'كرك|3'],
  kunafa_sweets: ['كنافة نابلسية|15', 'بقلاوة|5', 'بسبوسة|8', 'قهوة عربية|5'],
  syrian_cuisine: ['شاورما سورية|8', 'كباب مشكل|45', 'كبة|12', 'فتوش|10'],
  egyptian_cuisine: ['كشري|12', 'فول مصري|8', 'ملوخية|35', 'فطير|25'],
  sudanese_cuisine: ['شية|18', 'ملاح|30', 'عصيدة|20', 'جبنة|5'],
  donuts: ['جليزد|6', 'شوكولاتة|8', 'لوتس محشي|12', 'بوكس 6|30'],
  waffle_pancake: ['وافل كلاسيك|15', 'بان كيك|18', 'فرنش توست|22', 'شوكو ساخنة|14'],
  chocolate_shop: ['ترافل|5', 'براوني|12', 'فوندو|45', 'علبة 12|55'],
  cake_shop: ['كب كيك|8', 'قطعة كيك|15', 'تشيز كيك|20', 'ميني كيك|45'],
  catering: ['وجبة فردية|25', 'بوفيه 30|1500', 'ضيافة قهوة|500', 'طاولة حلى|800'],
  buffet_restaurant: ['فطور|35', 'غداء/عشاء|65', 'بحري|85', 'VIP|150'],
  ice_cream_gelato: ['فانيلا|12', 'شوكولاتة|14', 'فراولة|12', 'مانجو|14'],
  western_desserts: ['تشيز كيك|22', 'تيراميسو|25', 'براوني|18', 'كريم بروليه|20'],
  crepes: ['نوتيلا|15', 'لوتس|18', 'فراولة|14', 'جبنة وزعتر|10'],
  hasawi_food: ['مجبوس|38', 'هريسة|25', 'مكبوس|35', 'لقيمات|12'],
  turkish: ['كباب أضنة|42', 'إسكندر|38', 'لحم بعجين|8', 'بقلاوة|15'],
  mexican: ['تاكو|12', 'بوريتو|22', 'كيساديا|18', 'ناتشوز|15'],
  lebanese: ['تبولة|12', 'كبة|15', 'شيش طاووق|35', 'فتوش|10'],
  asian: ['نودلز|25', 'أرز مقلي|20', 'سبرنج رول|12', 'كاري|28'],
  bukhari_kitchen: ['رز بخاري|35', 'مندي|38', 'زربيان|42', 'سلطة|8'],
  meal_subscription: ['خطة أسبوعية|350', 'خطة شهرية|1200', 'وجبة فردية|35', 'سناك|15'],
  healthy_meal_prep: ['سلطة بروتين|28', 'بول صحي|25', 'وجبة متوازنة|32', 'سموذي|18'],
  keto_diet_restaurant: ['ستيك|45', 'سلطة أفوكادو|22', 'دجاج مشوي|28', 'سموذي كيتو|18'],
};

function SocialProofTicker({ lang }) {
  const [idx, setIdx] = useState(0);
  const [visible, setVisible] = useState(true);
  const [liveData, setLiveData] = useState(null);
  const [totalCount, setTotalCount] = useState(0);
  const isAr = lang === 'ar';
  const API = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetch(`${API}/api/social-proof/recent`).then(r => r.json()).then(data => {
      if (data && data.items && data.items.length > 0) {
        setLiveData(data.items);
        setTotalCount(data.total || 0);
      }
    }).catch(() => {});
  }, [API]);

  const staticMsgs = SOCIAL_PROOF_MESSAGES[isAr ? 'ar' : 'en'];
  const msgs = liveData || staticMsgs;

  useEffect(() => {
    const interval = setInterval(() => {
      setVisible(false);
      setTimeout(() => {
        setIdx(prev => (prev + 1) % msgs.length);
        setVisible(true);
      }, 400);
    }, 4000);
    return () => clearInterval(interval);
  }, [msgs.length]);

  const msg = msgs[idx];

  const formatTime = (mins) => {
    if (!mins || mins < 1) return isAr ? 'الآن' : 'just now';
    if (mins < 60) return isAr ? `قبل ${mins} دقائق` : `${mins} min ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return isAr ? `قبل ${hours} ساعة` : `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return isAr ? `قبل ${days} يوم` : `${days}d ago`;
  };

  let displayText;
  if (liveData) {
    const typeName = isAr ? msg.type_name_ar : msg.type_name_en;
    const timeText = formatTime(msg.mins_ago);
    displayText = isAr
      ? `${msg.name} بدأ تجربته (${typeName}) ${timeText}`
      : `${msg.name} started their trial (${typeName}) ${timeText}`;
  } else {
    const mins = [2, 5, 3, 8, 1, 4, 6, 2, 7, 3][idx % 10];
    displayText = isAr
      ? `${msg.name} من ${msg.city} ${msg.action} (${msg.type}) قبل ${mins} دقائق`
      : `${msg.name} from ${msg.city} ${msg.action} (${msg.type}) ${mins} min ago`;
  }

  const displayTotal = totalCount > 0 ? (totalCount >= 100 ? `${Math.floor(totalCount / 50) * 50}+` : `${totalCount}`) : null;

  return (
    <div className="mt-3 flex items-center gap-3" data-testid="social-proof-ticker">
      {displayTotal && (
        <span className="flex-shrink-0 px-2.5 py-1 rounded-full text-[10px] font-bold" style={{ background: 'rgba(16,185,129,0.1)', color: '#10B981', border: '1px solid rgba(16,185,129,0.15)' }} data-testid="social-proof-counter">
          {displayTotal} {isAr ? 'مشروع جرّب المنصة' : 'businesses tried'}
        </span>
      )}
      <div className="h-6 overflow-hidden flex-1">
        <div className="flex items-center gap-2 transition-all duration-400" style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(8px)' }}>
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse flex-shrink-0" />
          <span className="text-[11px] truncate" style={{ color: 'rgba(var(--lp-rgb),0.35)' }}>
            {displayText}
          </span>
        </div>
      </div>
    </div>
  );
}

function CountdownBanner({ lang }) {
  const isAr = lang === 'ar';
  const [time, setTime] = useState({ h: 0, m: 0, s: 0 });

  useEffect(() => {
    let deadline = localStorage.getItem('zouqai_offer_deadline');
    if (!deadline) {
      deadline = Date.now() + 48 * 60 * 60 * 1000;
      localStorage.setItem('zouqai_offer_deadline', String(deadline));
    }
    deadline = Number(deadline);
    const tick = () => {
      const diff = Math.max(0, deadline - Date.now());
      setTime({ h: Math.floor(diff / 3600000), m: Math.floor((diff % 3600000) / 60000), s: Math.floor((diff % 60000) / 1000) });
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);

  const pad = n => String(n).padStart(2, '0');

  return (
    <div data-anim="reveal" data-testid="countdown-banner" className="max-w-3xl mx-auto text-center p-6 md:p-8 rounded-2xl"
      style={{ background: 'linear-gradient(135deg, rgba(217,119,6,0.06), rgba(239,68,68,0.04))', border: '1px solid rgba(217,119,6,0.15)' }}>
      <div className="flex items-center justify-center gap-2 mb-3">
        <Clock className="w-5 h-5 text-amber-400" />
        <span className="text-sm md:text-base font-bold" style={{ fontFamily: 'Cairo, sans-serif' }}>
          {isAr ? 'عرض محدود - خصم 30% على الباقة الاحترافية' : 'Limited Offer - 30% Off Pro Plan'}
        </span>
      </div>
      <div className="flex items-center justify-center gap-3 mb-4" dir="ltr">
        {[{ v: pad(time.h), l: isAr ? 'ساعة' : 'Hours' }, { v: pad(time.m), l: isAr ? 'دقيقة' : 'Min' }, { v: pad(time.s), l: isAr ? 'ثانية' : 'Sec' }].map((u, i) => (
          <div key={i} className="flex items-center gap-3">
            <div className="text-center">
              <div className="text-2xl md:text-3xl font-black text-amber-400 tabular-nums" style={{ fontFamily: 'monospace' }}>{u.v}</div>
              <div className="text-[9px] mt-0.5" style={{ color: 'rgba(var(--lp-rgb),0.35)' }}>{u.l}</div>
            </div>
            {i < 2 && <span className="text-xl font-bold text-amber-400/40 -mt-3">:</span>}
          </div>
        ))}
      </div>
      <Link to="/demo" data-testid="countdown-cta" className="inline-flex items-center justify-center gap-2 px-6 py-2.5 rounded-xl text-xs font-bold text-white transition-all hover:scale-[1.02]"
        style={{ background: 'linear-gradient(135deg, #D97706, #EA580C)', boxShadow: '0 4px 16px rgba(217,119,6,0.3)' }}>
        <Sparkles className="w-3.5 h-3.5" /> {isAr ? 'استفد من العرض الآن' : 'Claim Offer Now'}
      </Link>
    </div>
  );
}

function DashboardMockup({ type, lang }) {
  const isAr = lang === 'ar';
  const raw = MOCK_MENUS[type] || MOCK_MENUS._default;
  const items = raw.map(s => { const [n, p] = s.split('|'); return { n, p }; });
  const bars = [40, 65, 45, 80, 55, 70, 90, 60, 75, 50, 85, 65];

  return (
    <div className="relative" data-testid="hero-dashboard-mockup">
      <div className="absolute -inset-8 rounded-3xl" style={{ background: 'radial-gradient(circle, rgba(217,119,6,0.12), transparent 70%)', filter: 'blur(40px)' }} />
      <div className="relative rounded-2xl overflow-hidden shadow-2xl" style={{ background: 'rgba(var(--lp-rgb),0.03)', border: '1px solid rgba(var(--lp-rgb),0.1)', backdropFilter: 'blur(20px)' }}>
        <div className="flex items-center px-4 py-2" style={{ background: 'rgba(var(--lp-rgb),0.04)', borderBottom: '1px solid rgba(var(--lp-rgb),0.06)' }}>
          <div className="flex gap-1.5">
            <div className="w-2 h-2 rounded-full bg-red-400/60" />
            <div className="w-2 h-2 rounded-full bg-yellow-400/60" />
            <div className="w-2 h-2 rounded-full bg-green-400/60" />
          </div>
          <div className="flex-1 mx-4">
            <div className="mx-auto max-w-[160px] h-5 rounded-md flex items-center justify-center text-[9px]" style={{ background: 'rgba(var(--lp-rgb),0.05)', color: 'rgba(var(--lp-rgb),0.3)' }}>
              app.zouq.ai/dashboard
            </div>
          </div>
        </div>
        <div className="flex" style={{ minHeight: 260 }}>
          <div className="w-10 py-3 flex flex-col items-center gap-2.5" style={{ background: 'rgba(var(--lp-rgb),0.02)', borderInlineEnd: '1px solid rgba(var(--lp-rgb),0.06)' }}>
            {[BarChart3, ShoppingCart, FileText, Users].map((Icon, i) => (
              <div key={i} className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: i === 0 ? 'rgba(217,119,6,0.15)' : 'transparent' }}>
                <Icon className="w-3 h-3" style={{ color: i === 0 ? '#D97706' : 'rgba(var(--lp-rgb),0.2)' }} />
              </div>
            ))}
          </div>
          <div className="flex-1 p-3">
            <div className="grid grid-cols-3 gap-2 mb-3">
              {[{ v: '47', l: isAr ? 'طلب' : 'Orders', c: '#10B981' }, { v: '٨,٩٢٠', l: isAr ? 'ر.س' : 'SAR', c: '#D97706' }, { v: '24', l: isAr ? 'صنف' : 'Items', c: '#6366F1' }].map((s, i) => (
                <div key={i} className="rounded-lg p-2 text-center" style={{ background: `${s.c}08`, border: `1px solid ${s.c}15` }}>
                  <div className="text-xs font-bold" style={{ color: s.c }}>{s.v}</div>
                  <div className="text-[8px] mt-0.5" style={{ color: 'rgba(var(--lp-rgb),0.3)' }}>{s.l}</div>
                </div>
              ))}
            </div>
            <div className="text-[9px] font-bold mb-2 px-0.5" style={{ color: 'rgba(var(--lp-rgb),0.4)' }}>{isAr ? 'القائمة' : 'Menu'}</div>
            <div className="grid grid-cols-2 gap-1.5" key={type || 'default'}>
              {items.map((item, i) => (
                <div key={i} className="rounded-lg p-2 flex items-center justify-between" style={{ background: 'rgba(var(--lp-rgb),0.04)', border: '1px solid rgba(var(--lp-rgb),0.06)', animation: `fadeSlideIn 0.3s ease-out ${i * 0.08}s both` }}>
                  <span className="text-[10px] font-medium truncate" style={{ color: 'rgba(var(--lp-rgb),0.6)' }}>{item.n}</span>
                  <span className="text-[9px] font-bold flex-shrink-0" style={{ color: '#D97706' }}>{item.p}</span>
                </div>
              ))}
            </div>
            <div className="mt-3 flex items-end gap-0.5 h-8 px-0.5">
              {bars.map((h, i) => (
                <div key={i} className="flex-1 rounded-sm" style={{ height: `${h}%`, background: i === 6 ? '#D97706' : 'rgba(217,119,6,0.15)', transformOrigin: 'bottom', animation: `growUp 0.5s ease-out ${i * 0.04}s both` }} />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function LandingPage() {
  const { lang, isAr, dir, toggleLanguage } = useLanguage();
  const { t } = useTranslation();
  const [mobileMenu, setMobileMenu] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);
  const [dark, setDark] = useState(() => document.documentElement.classList.contains('dark'));
  const [magicType, setMagicType] = useState('');
  const [magicCity, setMagicCity] = useState('');
  const [activeFeatureTab, setActiveFeatureTab] = useState('restaurant');

  // A/B Test completed: Variant B won (26.1% vs 16.1% conversion)
  const counterRefs = useRef([]);
  const l = (ar, en) => L(ar, en, lang);

  const toggleTheme = () => {
    const isDark = document.documentElement.classList.toggle('dark');
    setDark(isDark);
    localStorage.setItem('zouqai_theme', isDark ? 'dark' : 'light');
  };

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.from('[data-anim="hero-text"]', { y: 40, opacity: 0, duration: 0.8, stagger: 0.12, ease: 'power3.out' });
      gsap.from('[data-anim="hero-visual"]', { x: isAr ? -50 : 50, opacity: 0, duration: 1, delay: 0.4, ease: 'power3.out' });

      counterRefs.current.forEach(el => {
        if (!el) return;
        const target = parseInt(el.dataset.target) || 0;
        ScrollTrigger.create({ trigger: el, start: 'top 85%', onEnter: () => { gsap.to(el, { innerText: target, duration: 2, snap: { innerText: 1 }, ease: 'power2.out' }); } });
      });

      document.querySelectorAll('[data-anim="reveal"]').forEach(el => {
        gsap.fromTo(el, { y: 30, opacity: 0 }, { scrollTrigger: { trigger: el, start: 'top 85%', toggleActions: 'play none none none' }, y: 0, opacity: 1, duration: 0.6, ease: 'power2.out' });
      });

      document.querySelectorAll('[data-anim="stagger-children"]').forEach(parent => {
        gsap.fromTo(parent.children, { y: 24, opacity: 0 }, { scrollTrigger: { trigger: parent, start: 'top 85%', toggleActions: 'play none none none' }, y: 0, opacity: 1, duration: 0.5, stagger: 0.08, ease: 'power2.out' });
      });

      setTimeout(() => {
        document.querySelectorAll('[data-anim="reveal"], [data-anim="stagger-children"] > *').forEach(el => {
          if (parseFloat(getComputedStyle(el).opacity) < 0.1) { el.style.opacity = '1'; el.style.transform = 'none'; }
        });
      }, 2000);
    });
    return () => ctx.revert();
  }, []);

  // A/B tracking disabled (test concluded)

  const scrollTo = (id) => { document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' }); setMobileMenu(false); };

  // Build FAQPage Schema.org from the bilingual FAQS array
  const faqSchema = buildFAQSchema(FAQS.map(f => f[isAr ? 'ar' : 'en']));
  const breadcrumbSchema = buildBreadcrumb([{ name: isAr ? 'الرئيسية' : 'Home', url: SITE.url }]);

  return (
    <>
      <SEOHead
        title="ذوق AI - منصة المطاعم الذكية بالذكاء الاصطناعي #1 في السعودية والخليج"
        titleEn="Zouq AI - #1 AI Restaurant Management Platform in Saudi Arabia & GCC"
        description="منصة ذكاء اصطناعي شاملة لإدارة المطاعم والكافيهات والعربات المتنقلة. POS، شاشة مطبخ، فوترة زاتكا، ووكيل صوتي بالعامية السعودية. ابدأ مجاناً في 60 ثانية."
        descriptionEn="Comprehensive AI platform for managing restaurants, cafés, and food trucks. POS, kitchen display, ZATCA invoicing, and a Saudi-dialect voice agent. Start free in 60 seconds."
        path="/"
        keywords={[
          'ذوق AI', 'Zouq AI', 'نظام إدارة مطاعم', 'برنامج كاشير', 'POS مطعم',
          'شاشة مطبخ', 'فوترة زاتكا', 'ZATCA invoicing', 'AI restaurant management',
          'وكيل طلبات صوتي', 'voice ordering agent', 'مطاعم السعودية', 'GCC restaurant POS',
          'برنامج كافيهات', 'café management', 'نظام عربة متنقلة', 'food truck POS',
        ]}
        schemas={[faqSchema, breadcrumbSchema]}
        lang={lang}
      />
      <div style={{ background: 'var(--lp-bg)', color: 'var(--lp-text)', fontFamily: "'IBM Plex Sans Arabic', sans-serif", minHeight: '100vh', transition: 'background 0.3s, color 0.3s' }} dir={dir}>

      {/* ====== NAV ====== */}
      <nav className="fixed top-0 left-0 right-0 z-50" style={{ background: 'var(--lp-nav-bg)', backdropFilter: 'blur(16px)', borderBottom: '1px solid var(--lp-nav-border)', transition: 'background 0.3s' }}>
        <div className="max-w-6xl mx-auto px-4 h-14 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="nav-logo">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center shadow-lg shadow-amber-500/20">
              <ChefHat className="w-4 h-4 text-white" />
            </div>
            <span className="font-bold text-base" style={{ fontFamily: 'Cairo, sans-serif' }}>Zouq AI</span>
          </Link>
          <div className="hidden md:flex items-center gap-6 text-sm" style={{ color: 'rgba(var(--lp-rgb),0.5)' }}>
            <Link to="/restaurants" data-testid="nav-restaurants-link" className="hover:opacity-90 transition-opacity">{l('مطاعم', 'Restaurants')}</Link>
            <Link to="/cafes" data-testid="nav-cafes-link" className="hover:opacity-90 transition-opacity">{l('كافيهات', 'Cafés')}</Link>
            <Link to="/food-trucks" data-testid="nav-food-trucks-link" className="hover:opacity-90 transition-opacity">{l('عربات متنقلة', 'Food Trucks')}</Link>
            <button onClick={() => scrollTo('features')} data-testid="nav-features-link" className="hover:opacity-90 transition-opacity">{t('landing.navFeatures')}</button>
            <button onClick={() => scrollTo('pricing')} data-testid="nav-pricing-link" className="hover:opacity-90 transition-opacity">{t('landing.navPricing')}</button>
            <button onClick={() => scrollTo('faq')} data-testid="nav-faq-link" className="hover:opacity-90 transition-opacity">{t('landing.navFaq')}</button>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={toggleTheme} data-testid="landing-theme-toggle" className="p-1.5 rounded-md text-[rgba(var(--lp-rgb),0.4)] hover:text-[rgba(var(--lp-rgb),0.7)] transition-colors" style={{ background: 'rgba(var(--lp-rgb),0.05)' }}>
              {dark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </button>
            <button onClick={toggleLanguage} data-testid="lang-toggle" className="px-2.5 py-1 rounded-md text-xs transition-colors" style={{ background: 'rgba(var(--lp-rgb),0.05)', color: 'rgba(var(--lp-rgb),0.4)' }}>
              {lang === 'ar' ? 'EN' : 'عربي'}
            </button>
            <Link to="/login" data-testid="nav-login-btn" className="hidden md:inline-flex px-4 py-1.5 rounded-lg text-xs font-semibold transition-colors" style={{ background: 'rgba(var(--lp-rgb),0.06)', color: 'rgba(var(--lp-rgb),0.7)' }}>
              {t('landing.login')}
            </Link>
            <Link to="/login" data-testid="nav-cta" className="hidden md:inline-flex px-4 py-1.5 rounded-lg text-xs font-bold text-white bg-gradient-to-l from-amber-500 to-orange-600 shadow-lg shadow-amber-500/20 hover:shadow-amber-500/40 transition-shadow">
              {t('landing.startFree')}
            </Link>
            <button onClick={() => setMobileMenu(m => !m)} data-testid="mobile-menu-toggle" className="md:hidden p-1.5" style={{ color: 'rgba(var(--lp-rgb),0.6)' }}>
              {mobileMenu ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
        {mobileMenu && (
          <div className="md:hidden px-4 pb-4 space-y-2" style={{ background: 'rgba(var(--lp-rgb),0.03)' }} data-testid="mobile-menu-panel">
            <Link to="/restaurants" data-testid="mobile-nav-restaurants" onClick={() => setMobileMenu(false)} className="block w-full text-start px-3 py-2 text-sm rounded-lg transition-colors" style={{ color: 'rgba(var(--lp-rgb),0.6)' }}>{l('للمطاعم', 'For Restaurants')}</Link>
            <Link to="/cafes" data-testid="mobile-nav-cafes" onClick={() => setMobileMenu(false)} className="block w-full text-start px-3 py-2 text-sm rounded-lg transition-colors" style={{ color: 'rgba(var(--lp-rgb),0.6)' }}>{l('للكافيهات', 'For Cafés')}</Link>
            <Link to="/food-trucks" data-testid="mobile-nav-food-trucks" onClick={() => setMobileMenu(false)} className="block w-full text-start px-3 py-2 text-sm rounded-lg transition-colors" style={{ color: 'rgba(var(--lp-rgb),0.6)' }}>{l('للعربات المتنقلة', 'For Food Trucks')}</Link>
            {[['features', t('landing.navFeatures')], ['pricing', t('landing.navPricing')], ['faq', t('landing.navFaq')]].map(([id, label]) => (
              <button key={id} onClick={() => scrollTo(id)} data-testid={`mobile-nav-${id}`} className="block w-full text-start px-3 py-2 text-sm rounded-lg transition-colors" style={{ color: 'rgba(var(--lp-rgb),0.6)' }}>{label}</button>
            ))}
            <div className="pt-2 space-y-2" style={{ borderTop: '1px solid rgba(var(--lp-rgb),0.06)' }}>
              <Link to="/login" data-testid="mobile-login-btn" className="block w-full text-center px-3 py-2 text-sm rounded-lg transition-colors" style={{ color: 'rgba(var(--lp-rgb),0.6)' }}>{t('landing.login')}</Link>
              <Link to="/demo" data-testid="mobile-demo-btn" className="block w-full text-center px-3 py-2 text-sm font-bold text-white bg-gradient-to-l from-amber-500 to-orange-600 rounded-lg">{l('جرب مجاناً', 'Try Free')}</Link>
            </div>
          </div>
        )}
      </nav>

      <main>
        {/* ====== HERO ====== */}
        <section className="pt-24 pb-12 md:pt-32 md:pb-20 px-4 relative overflow-hidden">
          <div className="absolute top-20 right-[10%] w-72 h-72 md:w-96 md:h-96 rounded-full" style={{ background: 'radial-gradient(circle, #D97706, transparent)', filter: 'blur(100px)', opacity: 'var(--lp-glow-opacity)' }} />
          <div className="absolute bottom-10 left-[20%] w-48 h-48 md:w-72 md:h-72 rounded-full opacity-10" style={{ background: 'radial-gradient(circle, #6366F1, transparent)', filter: 'blur(80px)' }} />
          {/* Subtle grid pattern */}
          <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'linear-gradient(rgba(255,255,255,.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.1) 1px, transparent 1px)', backgroundSize: '60px 60px' }} />

          <div className="max-w-6xl mx-auto relative z-10">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
              <div className="text-center lg:text-start">
                <div data-anim="hero-text" className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-5 text-xs font-semibold backdrop-blur-sm" style={{ background: 'rgba(217,119,6,0.08)', border: '1px solid rgba(217,119,6,0.2)', color: '#D97706' }}>
                  <Sparkles className="w-3.5 h-3.5" /> {l('منصة إدارة المشاريع الغذائية #1 في السعودية', '#1 Food Business Platform in Saudi Arabia')}
                </div>

                <h1 data-anim="hero-text" className="text-3xl md:text-4xl lg:text-5xl font-extrabold leading-tight mb-5" style={{ fontFamily: 'Cairo, sans-serif', letterSpacing: '-0.02em' }}>
                  {l('حوّل مشروعك إلى', 'Transform Your Business Into')}{' '}
                  <span className="bg-gradient-to-l from-amber-400 via-orange-500 to-amber-400 bg-clip-text bg-[length:200%_auto]" style={{ WebkitTextFillColor: 'transparent', animation: 'shimmer 3s linear infinite' }}>
                    {l('آلة ذكية تدير نفسها', 'A Self-Managing Machine')}
                  </span>
                </h1>

                <p data-anim="hero-text" className="text-base md:text-lg leading-relaxed mb-6 max-w-lg mx-auto lg:mx-0" style={{ color: 'rgba(var(--lp-rgb),0.5)' }}>
                  {l(
                    'نظام ذكي شامل يدير مشروعك بالكامل - من الطلبات والمحاسبة إلى الفوترة والتوصيل. مصمم خصيصاً للسوق السعودي.',
                    'A comprehensive AI system that manages your entire business - from orders and accounting to invoicing and delivery. Built for Saudi Arabia.'
                  )}
                </p>

                {/* Magic Line - Primary CTA */}
                <div data-anim="hero-text" className="p-4 md:p-5 rounded-2xl mb-5 max-w-lg mx-auto lg:mx-0" data-testid="magic-line"
                  style={{ background: 'rgba(var(--lp-rgb),0.04)', border: '1px solid rgba(var(--lp-rgb),0.08)' }}>
                  <p className="text-[11px] font-bold uppercase tracking-wider mb-3 flex items-center gap-1.5" style={{ color: 'rgba(var(--lp-rgb),0.35)' }}>
                    <Zap className="w-3.5 h-3.5" style={{ color: '#D97706' }} /> {l('جرّب مشروعك الآن - بدون تسجيل', 'Try your business now - no signup needed')}
                  </p>
                  <div className="flex flex-col sm:flex-row items-stretch gap-2.5 mb-3">
                    <div className="flex items-center gap-2 flex-1">
                      <span className="text-xs font-medium whitespace-nowrap" style={{ color: 'rgba(var(--lp-rgb),0.5)' }}>{l('أنا صاحب', "I own a")}</span>
                      <select value={magicType} onChange={e => setMagicType(e.target.value)} data-testid="magic-type-select"
                        className="flex-1 h-10 px-3 rounded-xl text-sm font-medium appearance-none cursor-pointer"
                        style={{ background: 'rgba(var(--lp-rgb),0.06)', border: '1px solid rgba(var(--lp-rgb),0.1)', color: magicType ? '#D97706' : 'rgba(var(--lp-rgb),0.35)' }}>
                        <option value="">{l('اختر النوع...', 'Choose type...')}</option>
                        <optgroup label={l('وجبات سريعة', 'Fast Food')}>
                          <option value="shawarma">{l('🌯 شاورما', '🌯 Shawarma')}</option>
                          <option value="burger">{l('🍔 برجر', '🍔 Burgers')}</option>
                          <option value="pizza">{l('🍕 بيتزا', '🍕 Pizza')}</option>
                          <option value="fried_chicken">{l('🍗 دجاج مقلي', '🍗 Fried Chicken')}</option>
                          <option value="sandwiches">{l('🥪 ساندويتشات', '🥪 Sandwiches')}</option>
                          <option value="crepes">{l('🥞 كريب', '🥞 Crepes')}</option>
                          <option value="broasted">{l('🍗 بروستد', '🍗 Broasted')}</option>
                          <option value="falafel_hummus">{l('🧆 فلافل وحمص', '🧆 Falafel & Hummus')}</option>
                        </optgroup>
                        <optgroup label={l('سعودي وعربي', 'Saudi & Arabic')}>
                          <option value="kabsa_mandi">{l('🍚 كبسة ومندي', '🍚 Kabsa & Mandi')}</option>
                          <option value="saudi_grills">{l('🥩 مشويات سعودية', '🥩 Saudi Grills')}</option>
                          <option value="foul_tamees">{l('🫘 فول وتميس', '🫘 Foul & Tamees')}</option>
                          <option value="hejazi_food">{l('🥘 مطبخ حجازي', '🥘 Hejazi')}</option>
                          <option value="traditional_saudi">{l('🍲 أكلات شعبية', '🍲 Traditional Saudi')}</option>
                          <option value="yemeni_food">{l('🫕 مطبخ يمني', '🫕 Yemeni')}</option>
                          <option value="hasawi_food">{l('🍛 مطبخ حساوي', '🍛 Hasawi')}</option>
                          <option value="pastries_fatayer">{l('🥟 معجنات وفطائر', '🥟 Pastries & Fatayer')}</option>
                        </optgroup>
                        <optgroup label={l('مشروبات وحلويات', 'Drinks & Desserts')}>
                          <option value="specialty_coffee">{l('☕ كافيه مختص', '☕ Specialty Café')}</option>
                          <option value="fresh_juices">{l('🥤 عصائر', '🥤 Juices')}</option>
                          <option value="ice_cream_gelato">{l('🍦 آيس كريم وجيلاتو', '🍦 Ice Cream & Gelato')}</option>
                          <option value="arabic_bakery">{l('🧁 مخبز عربي', '🧁 Arabic Bakery')}</option>
                          <option value="western_desserts">{l('🍰 حلويات غربية', '🍰 Western Desserts')}</option>
                          <option value="bubble_tea">{l('🧋 بوبل تي', '🧋 Bubble Tea')}</option>
                          <option value="kunafa_sweets">{l('🍮 كنافة وحلويات شرقية', '🍮 Kunafa & Sweets')}</option>
                          <option value="donuts">{l('🍩 دونات', '🍩 Donuts')}</option>
                          <option value="waffle_pancake">{l('🧇 وافل وبان كيك', '🧇 Waffle & Pancake')}</option>
                          <option value="chocolate_shop">{l('🍫 شوكولاتة', '🍫 Chocolate')}</option>
                          <option value="cake_shop">{l('🎂 كيك وتورتات', '🎂 Cakes')}</option>
                        </optgroup>
                        <optgroup label={l('مطابخ عالمية', 'International')}>
                          <option value="seafood">{l('🦐 مأكولات بحرية', '🦐 Seafood')}</option>
                          <option value="sushi">{l('🍣 سوشي', '🍣 Sushi')}</option>
                          <option value="italian">{l('🍝 إيطالي', '🍝 Italian')}</option>
                          <option value="indian">{l('🍛 هندي', '🍛 Indian')}</option>
                          <option value="turkish">{l('🥙 تركي', '🥙 Turkish')}</option>
                          <option value="mexican">{l('🌮 مكسيكي', '🌮 Mexican')}</option>
                          <option value="lebanese">{l('🇱🇧 لبناني', '🇱🇧 Lebanese')}</option>
                          <option value="asian">{l('🍜 آسيوي', '🍜 Asian')}</option>
                          <option value="syrian_cuisine">{l('🇸🇾 مطبخ سوري', '🇸🇾 Syrian')}</option>
                          <option value="egyptian_cuisine">{l('🇪🇬 مطبخ مصري', '🇪🇬 Egyptian')}</option>
                          <option value="sudanese_cuisine">{l('🇸🇩 مطبخ سوداني', '🇸🇩 Sudanese')}</option>
                        </optgroup>
                        <optgroup label={l('خدمات حديثة', 'Modern Services')}>
                          <option value="cloud_kitchen">{l('🏭 مطبخ سحابي', '🏭 Cloud Kitchen')}</option>
                          <option value="bukhari_kitchen">{l('🍚 مطعم بخاري', '🍚 Bukhari')}</option>
                          <option value="meal_subscription">{l('📦 اشتراك وجبات', '📦 Meal Subscription')}</option>
                          <option value="healthy_meal_prep">{l('🥗 طعام صحي', '🥗 Healthy Meals')}</option>
                          <option value="keto_diet_restaurant">{l('🥑 دايت وكيتو', '🥑 Keto & Diet')}</option>
                          <option value="food_truck">{l('🚚 فود ترك', '🚚 Food Truck')}</option>
                          <option value="catering">{l('🎉 تموين وحفلات', '🎉 Catering')}</option>
                          <option value="buffet_restaurant">{l('🍽️ بوفيه مفتوح', '🍽️ Buffet')}</option>
                        </optgroup>
                      </select>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-medium whitespace-nowrap" style={{ color: 'rgba(var(--lp-rgb),0.5)' }}>{l('في', 'in')}</span>
                      <select value={magicCity} onChange={e => setMagicCity(e.target.value)} data-testid="magic-city-select"
                        className="h-10 px-3 rounded-xl text-sm font-medium appearance-none cursor-pointer"
                        style={{ background: 'rgba(var(--lp-rgb),0.06)', border: '1px solid rgba(var(--lp-rgb),0.1)', color: magicCity ? '#D97706' : 'rgba(var(--lp-rgb),0.35)', minWidth: 100 }}>
                        <option value="">{l('المدينة', 'City')}</option>
                        <option value="riyadh">{l('الرياض', 'Riyadh')}</option>
                        <option value="jeddah">{l('جدة', 'Jeddah')}</option>
                        <option value="dammam">{l('الدمام', 'Dammam')}</option>
                        <option value="makkah">{l('مكة', 'Makkah')}</option>
                        <option value="madinah">{l('المدينة', 'Madinah')}</option>
                      </select>
                    </div>
                  </div>
                  <Link to={`/demo${magicType ? `?type=${magicType}` : ''}${magicCity ? `${magicType ? '&' : '?'}city=${magicCity}` : ''}`}
                    data-testid="magic-start-btn"
                    className="w-full h-11 rounded-xl text-sm font-bold text-white flex items-center justify-center gap-2 transition-all hover:scale-[1.01] active:scale-[0.99]"
                    style={{ background: 'linear-gradient(135deg, #D97706, #EA580C)', boxShadow: '0 6px 24px rgba(217,119,6,0.3)' }}>
                    <Sparkles className="w-4 h-4" />
                    {l('جرّب مجاناً خلال 10 ثوان', 'Try free in 10 seconds')}
                  </Link>
                  {/* Social Proof */}
                  <SocialProofTicker lang={lang} />
                </div>

                {/* Stats */}
                <div data-anim="hero-text" className="flex gap-8 md:gap-12 justify-center lg:justify-start">
                  {[{ n: 500, label: l('مشروع', 'Business'), s: '+' }, { n: 10000, label: l('طلب شهري', 'Monthly Orders'), s: '+' }, { n: 99, label: l('رضا العملاء', 'Satisfaction'), s: '%' }].map((s, i) => (
                    <div key={i}>
                      <div className="text-xl md:text-2xl font-extrabold text-amber-400">
                        <span ref={el => counterRefs.current[i] = el} data-target={s.n}>0</span>{s.s}
                      </div>
                      <div className="text-[11px]" style={{ color: 'rgba(var(--lp-rgb),0.3)' }}>{s.label}</div>
                    </div>
                  ))}
                </div>

                {/* Secondary links */}
                <div data-anim="hero-text" className="flex items-center gap-4 mt-5 justify-center lg:justify-start">
                  <Link to="/login" data-testid="hero-cta-btn" className="text-xs font-medium transition-colors hover:text-amber-400" style={{ color: 'rgba(var(--lp-rgb),0.4)' }}>
                    {l('لدي حساب بالفعل → دخول', 'Already have an account? → Login')}
                  </Link>
                </div>
              </div>
              <div className="hidden lg:block" data-anim="hero-visual">
                <DashboardMockup type={magicType} lang={lang} />
              </div>
            </div>
          </div>
        </section>

        {/* ====== TRUST BAR ====== */}
        <section className="py-6 md:py-8 px-4 overflow-hidden" style={{ borderBottom: '1px solid rgba(var(--lp-rgb),0.04)' }}>
          <div className="max-w-5xl mx-auto">
            <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-3 md:gap-x-12" data-testid="trust-bar">
              {[
                { icon: Shield, color: '#10B981', ar: 'معتمد من ZATCA', en: 'ZATCA Certified' },
                { icon: Brain, color: '#6366F1', ar: 'مدعوم بالذكاء الاصطناعي', en: 'AI-Powered' },
                { icon: Globe, color: '#D97706', ar: 'عربي بالكامل', en: 'Fully Arabic' },
                { icon: Zap, color: '#EC4899', ar: '46 نوع مشروع', en: '46 Business Types' },
                { icon: Clock, color: '#0D9488', ar: 'جاهز في دقائق', en: 'Ready in Minutes' },
              ].map((b, i) => {
                const Icon = b.icon;
                return (
                  <div key={i} data-testid={`trust-badge-${i}`} className="flex items-center gap-2 transition-opacity hover:opacity-100" style={{ opacity: 0.55 }}>
                    <Icon className="w-4 h-4" style={{ color: b.color }} />
                    <span className="text-[11px] md:text-xs font-semibold whitespace-nowrap">{l(b.ar, b.en)}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* ====== HOW IT WORKS ====== */}
        <section className="py-16 md:py-24 px-4 relative" style={{ background: 'rgba(var(--lp-rgb),0.01)' }}>
          <div className="max-w-6xl mx-auto">
            <div data-anim="reveal" className="text-center mb-12 md:mb-16">
              <h2 className="text-2xl md:text-3xl font-extrabold mb-3" style={{ fontFamily: 'Cairo, sans-serif' }}>
                {l('ابدأ في', 'Start in')}{' '}
                <span className="bg-gradient-to-l from-amber-400 to-orange-500 bg-clip-text" style={{ WebkitTextFillColor: 'transparent' }}>
                  {l('3 خطوات بسيطة', '3 Simple Steps')}
                </span>
              </h2>
              <p className="text-sm md:text-base max-w-lg mx-auto" style={{ color: 'rgba(var(--lp-rgb),0.4)' }}>
                {l('من الصفر إلى نظام إدارة كامل في دقائق', 'From zero to a complete management system in minutes')}
              </p>
            </div>
            <div data-anim="stagger-children" className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 relative">
              <div className="hidden md:block absolute top-16 left-[20%] right-[20%] h-px" style={{ background: 'linear-gradient(90deg, transparent, rgba(217,119,6,0.3), transparent)' }} />
              {[
                { n: '1', icon: ChevronDown, color: '#D97706', ar: { t: 'اختر نوع مشروعك', d: 'حدد نوع مشروعك الغذائي من بين 46 نوع - مطعم، كافيه، بروستد، كنافة، فود ترك، وأكثر' }, en: { t: 'Choose Your Business', d: 'Select your food business type from 46 options - restaurant, café, broasted, kunafa, food truck, and more' } },
                { n: '2', icon: Sparkles, color: '#10B981', ar: { t: 'نجهز كل شيء فوراً', d: 'النظام يبني لك داشبورد كامل ببيانات مخصصة لنوع مشروعك - قائمة طعام، أصناف، وأسعار' }, en: { t: 'We Prepare Everything', d: 'The system builds a complete dashboard with custom data for your business - menu, items, and prices' } },
                { n: '3', icon: Zap, color: '#6366F1', ar: { t: 'ابدأ العمل مباشرة', d: 'نظامك جاهز من اليوم الأول - نقطة بيع، فوترة، محاسبة، وZouqBot كلهم يعملون' }, en: { t: 'Start Working Now', d: 'Your system is ready from day one - POS, invoicing, accounting, and ZouqBot all working' } },
              ].map((step, i) => {
                const Icon = step.icon;
                const info = step[lang] || step.en;
                return (
                  <div key={i} className="text-center relative" data-testid={`how-step-${i}`}>
                    <div className="w-16 h-16 rounded-2xl mx-auto mb-5 flex items-center justify-center relative" style={{ background: `${step.color}10`, border: `2px solid ${step.color}25` }}>
                      <span className="text-2xl font-black" style={{ color: step.color, fontFamily: 'Cairo, sans-serif' }}>{step.n}</span>
                      <div className="absolute -top-1 -right-1 w-6 h-6 rounded-full flex items-center justify-center" style={{ background: step.color }}>
                        <Icon className="w-3 h-3 text-white" />
                      </div>
                    </div>
                    <h3 className="text-base md:text-lg font-bold mb-2" style={{ fontFamily: 'Cairo, sans-serif' }}>{info.t}</h3>
                    <p className="text-xs md:text-sm leading-relaxed max-w-xs mx-auto" style={{ color: 'rgba(var(--lp-rgb),0.4)' }}>{info.d}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* ====== FEATURES ====== */}
        <section id="features" className="py-16 md:py-24 px-4">
          <div className="max-w-6xl mx-auto">
            <div data-anim="reveal" className="text-center mb-12 md:mb-16">
              <h2 className="text-2xl md:text-3xl font-extrabold mb-3" style={{ fontFamily: 'Cairo, sans-serif' }}>
                {l('كل ما يحتاجه مشروعك', 'Everything Your Business Needs')}{' '}
                <span style={{ color: '#D97706' }}>{l('في منصة واحدة', 'in One Platform')}</span>
              </h2>
              <p className="text-sm md:text-base max-w-lg mx-auto" style={{ color: 'rgba(var(--lp-rgb),0.4)' }}>{l('أدوات ذكية مصممة لـ 46 نوع من المشاريع الغذائية', 'Smart tools designed for 46 food business types')}</p>
              <div className="flex items-center justify-center gap-2 mt-6" data-testid="features-tabs">
                {TYPES.map(tp => {
                  const Icon = tp.icon;
                  const active = activeFeatureTab === tp.id;
                  return (
                    <button key={tp.id} data-testid={`features-tab-${tp.id}`} onClick={() => setActiveFeatureTab(tp.id)}
                      className="flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold transition-all duration-300"
                      style={{ background: active ? `${tp.color}15` : 'rgba(var(--lp-rgb),0.04)', border: `1.5px solid ${active ? tp.color + '40' : 'rgba(var(--lp-rgb),0.08)'}`, color: active ? tp.color : 'rgba(var(--lp-rgb),0.4)', transform: active ? 'scale(1.05)' : 'scale(1)' }}>
                      <Icon className="w-3.5 h-3.5" />
                      {tp[lang]?.name || tp.en.name}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5" data-testid="features-grid" key={activeFeatureTab}>
              {(FEATURES[activeFeatureTab] || FEATURES.restaurant).map((f, i) => {
                const Icon = f.icon;
                const info = f[lang] || f.en;
                return (
                  <article key={i} data-testid={`feature-${i}`} className="p-5 md:p-6 rounded-2xl transition-all duration-300 cursor-default group"
                    style={{ background: 'rgba(var(--lp-rgb),0.02)', border: '1px solid rgba(var(--lp-rgb),0.05)' }}
                    onMouseEnter={e => { e.currentTarget.style.background = `${f.color}08`; e.currentTarget.style.borderColor = `${f.color}25`; e.currentTarget.style.transform = 'translateY(-4px)'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'rgba(var(--lp-rgb),0.02)'; e.currentTarget.style.borderColor = 'rgba(var(--lp-rgb),0.05)'; e.currentTarget.style.transform = 'none'; }}>
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-4" style={{ background: `${f.color}15` }}>
                      <Icon className="w-5 h-5" style={{ color: f.color }} />
                    </div>
                    <h3 className="text-sm md:text-base font-bold mb-2" style={{ fontFamily: 'Cairo, sans-serif' }}>{info.t}</h3>
                    <p className="text-xs md:text-sm leading-relaxed" style={{ color: 'rgba(var(--lp-rgb),0.4)' }}>{info.d}</p>
                  </article>
                );
              })}
            </div>
          </div>
        </section>

        {/* ====== WHY ZOUQ AI ====== */}
        <section className="py-16 md:py-24 px-4" style={{ background: 'rgba(var(--lp-rgb),0.01)' }}>
          <div className="max-w-6xl mx-auto">
            <div data-anim="reveal" className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-extrabold mb-3" style={{ fontFamily: 'Cairo, sans-serif' }}>
                {l('لماذا', 'Why')}{' '}
                <span className="bg-gradient-to-l from-amber-400 to-orange-500 bg-clip-text" style={{ WebkitTextFillColor: 'transparent' }}>
                  {l('ذوق AI', 'Zouq AI')}
                </span>
                {l('؟', '?')}
              </h2>
              <p className="text-sm lp-text-40 max-w-md mx-auto">{l('4 أسباب تجعلنا الخيار الأول لمشاريع الأغذية في السعودية', '4 reasons we\'re the #1 choice for food businesses in Saudi Arabia')}</p>
            </div>
            <div data-anim="stagger-children" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {WHY_US.map((item, i) => {
                const Icon = item.icon;
                const info = item[lang] || item.en;
                return (
                  <div key={i} data-testid={`why-${i}`} className="p-5 md:p-6 rounded-2xl text-center transition-all duration-300"
                    style={{ background: 'rgba(var(--lp-rgb),0.02)', border: '1px solid rgba(var(--lp-rgb),0.05)' }}
                    onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.borderColor = `${item.color}25`; }}
                    onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.borderColor = 'rgba(var(--lp-rgb),0.05)'; }}>
                    <div className="w-12 h-12 rounded-2xl flex items-center justify-center mx-auto mb-4" style={{ background: `${item.color}12` }}>
                      <Icon className="w-6 h-6" style={{ color: item.color }} />
                    </div>
                    <h3 className="font-bold text-sm mb-2" style={{ fontFamily: 'Cairo, sans-serif' }}>{info.t}</h3>
                    <p className="text-xs lp-text-40 leading-relaxed">{info.d}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* ====== COMPARISON ====== */}
        <section className="py-16 md:py-24 px-4">
          <div className="max-w-4xl mx-auto">
            <div data-anim="reveal" className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-extrabold mb-3" style={{ fontFamily: 'Cairo, sans-serif' }}>
                {l('ذوق AI مقابل', 'Zouq AI vs')}{' '}
                <span style={{ color: 'rgba(var(--lp-rgb),0.3)' }}>{l('الأنظمة التقليدية', 'Traditional Systems')}</span>
              </h2>
            </div>
            <div data-anim="reveal" className="rounded-2xl overflow-hidden" data-testid="comparison-table" style={{ border: '1px solid rgba(var(--lp-rgb),0.08)' }}>
              <div className="grid grid-cols-3 text-center text-xs font-bold py-3" style={{ background: 'rgba(var(--lp-rgb),0.04)' }}>
                <div style={{ color: 'rgba(var(--lp-rgb),0.4)' }}>{l('المقارنة', 'Feature')}</div>
                <div className="text-amber-400">{l('ذوق AI', 'Zouq AI')}</div>
                <div style={{ color: 'rgba(var(--lp-rgb),0.3)' }}>{l('الأنظمة التقليدية', 'Traditional')}</div>
              </div>
              {[
                { ar: 'وقت التجهيز', en: 'Setup Time', zouq: { ar: 'دقائق', en: 'Minutes' }, other: { ar: 'أسابيع', en: 'Weeks' }, zouqWin: true },
                { ar: 'ذكاء اصطناعي', en: 'AI Capabilities', zouq: { ar: 'ZouqBot متكامل', en: 'Full ZouqBot' }, other: { ar: 'غير متوفر', en: 'Not Available' }, zouqWin: true },
                { ar: 'دعم العربية', en: 'Arabic Support', zouq: { ar: 'كامل + عامية', en: 'Full + Dialect' }, other: { ar: 'جزئي', en: 'Partial' }, zouqWin: true },
                { ar: 'توافق ZATCA', en: 'ZATCA Compliance', zouq: { ar: 'مدمج', en: 'Built-in' }, other: { ar: 'إضافة مدفوعة', en: 'Paid Add-on' }, zouqWin: true },
                { ar: 'التكلفة الشهرية', en: 'Monthly Cost', zouq: { ar: 'من 99 ر.س', en: 'From 99 SAR' }, other: { ar: '+500 ر.س', en: '500+ SAR' }, zouqWin: true },
                { ar: 'التحديثات', en: 'Updates', zouq: { ar: 'تلقائية ومجانية', en: 'Auto & Free' }, other: { ar: 'يدوية ومدفوعة', en: 'Manual & Paid' }, zouqWin: true },
              ].map((row, i) => (
                <div key={i} data-testid={`compare-row-${i}`} className="grid grid-cols-3 text-center text-xs py-3 items-center transition-colors"
                  style={{ borderTop: '1px solid rgba(var(--lp-rgb),0.05)', background: i % 2 === 0 ? 'transparent' : 'rgba(var(--lp-rgb),0.015)' }}>
                  <div className="font-medium" style={{ color: 'rgba(var(--lp-rgb),0.5)' }}>{l(row.ar, row.en)}</div>
                  <div className="font-bold flex items-center justify-center gap-1.5">
                    <Check className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0" />
                    <span className="text-emerald-400">{l(row.zouq.ar, row.zouq.en)}</span>
                  </div>
                  <div className="flex items-center justify-center gap-1.5" style={{ color: 'rgba(var(--lp-rgb),0.3)' }}>
                    <X className="w-3.5 h-3.5 text-red-400/50 flex-shrink-0" />
                    <span>{l(row.other.ar, row.other.en)}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ====== SEO SECTIONS ====== */}
        <section id="solutions" className="py-16 md:py-24 px-4">
          <div className="max-w-6xl mx-auto">
            <div data-anim="reveal" className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-extrabold mb-3" style={{ fontFamily: 'Cairo, sans-serif' }}>
                {l('حلول ذكية لكل أنواع المشاريع', 'Smart Solutions for Every Business Type')}
              </h2>
            </div>
            <div className="space-y-8">
              {SEO_SECTIONS.map((sec, i) => {
                const Icon = sec.icon;
                const info = sec[lang] || sec.en;
                return (
                  <div key={sec.id} data-anim="reveal" data-testid={`seo-${sec.id}`}
                    className="p-6 md:p-8 rounded-2xl transition-all duration-300"
                    style={{ background: `${sec.color}04`, border: `1px solid ${sec.color}15` }}>
                    <div className="flex flex-col md:flex-row md:items-start gap-5">
                      <div className="w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0" style={{ background: `${sec.color}12` }}>
                        <Icon className="w-7 h-7" style={{ color: sec.color }} />
                      </div>
                      <div className="flex-1">
                        <h2 className="text-lg md:text-xl font-extrabold mb-3" style={{ fontFamily: 'Cairo, sans-serif' }}>{info.title}</h2>
                        <p className="text-sm lp-text-45 leading-relaxed mb-4">{info.body}</p>
                        <Link to="/demo" data-testid={`seo-cta-${sec.id}`} className="inline-flex items-center gap-2 px-5 py-2 rounded-xl text-xs font-bold transition-all hover:opacity-90"
                          style={{ background: `${sec.color}15`, color: sec.color, border: `1px solid ${sec.color}25` }}>
                          {info.cta} <ArrowLeft className={`w-3.5 h-3.5 ${!isAr ? 'rotate-180' : ''}`} />
                        </Link>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* ====== DEMO CTA ====== */}
        <section className="py-16 md:py-24 px-4" style={{ background: 'rgba(var(--lp-rgb),0.01)' }}>
          <div data-anim="reveal" className="max-w-2xl mx-auto text-center">
            <Sparkles className="w-8 h-8 text-amber-400 mx-auto mb-4" />
            <h2 className="text-xl md:text-2xl font-extrabold mb-3" style={{ fontFamily: 'Cairo, sans-serif' }}>
              {l('جرب ذوق AI الآن مجاناً', 'Try Zouq AI Now - Free')}
            </h2>
            <p className="text-sm lp-text-40 mb-6">{l('46 نوع مشروع · 1200+ صنف · جاهز خلال ثوان', '46 business types · 1200+ items · ready in seconds')}</p>
            <Link to="/demo" data-testid="demo-cta-main"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-xl text-sm font-bold text-white transition-all hover:scale-[1.02]"
              style={{ background: 'linear-gradient(135deg, #D97706, #EA580C)', boxShadow: '0 8px 32px rgba(217,119,6,0.3)' }}>
              <Sparkles className="w-4 h-4" /> {l('ابدأ التجربة المجانية', 'Start Free Trial')}
            </Link>
            <p className="text-xs lp-text-30 mt-3">{l('بدون تسجيل · بدون بطاقة ائتمان · 14 يوم مجاناً', 'No signup · No credit card · 14 days free')}</p>
          </div>
        </section>

        {/* ====== COUNTDOWN OFFER ====== */}
        <section className="py-12 md:py-16 px-4">
          <CountdownBanner lang={lang} />
        </section>

        {/* ====== PRICING ====== */}
        <section id="pricing" className="py-16 md:py-24 px-4">
          <div className="max-w-6xl mx-auto">
            <div data-anim="reveal" className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-extrabold mb-3" style={{ fontFamily: 'Cairo, sans-serif' }}>{t('landing.pricingTitle')}</h2>
              <p className="text-sm lp-text-40">{l('أسعار تناسب كل الأحجام - ابدأ مجاناً بدون بطاقة ائتمان', 'Prices for every size - start free with no credit card')}</p>
            </div>
            <div data-anim="stagger-children" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {PLANS.map(p => {
                const info = p[lang] || p.en;
                return (
                  <div key={p.id} data-testid={`landing-plan-${p.id}`} className="p-5 md:p-6 rounded-2xl relative transition-all duration-300 group"
                    style={{ background: p.popular ? 'rgba(217,119,6,0.06)' : 'rgba(var(--lp-rgb),0.02)', border: `1.5px solid ${p.popular ? 'rgba(217,119,6,0.3)' : 'rgba(var(--lp-rgb),0.05)'}`, boxShadow: p.popular ? '0 8px 30px rgba(217,119,6,0.08)' : 'none' }}
                    onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-6px)'; e.currentTarget.style.boxShadow = p.popular ? '0 16px 40px rgba(217,119,6,0.15)' : '0 8px 30px rgba(0,0,0,0.1)'; }}
                    onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = p.popular ? '0 8px 30px rgba(217,119,6,0.08)' : 'none'; }}>
                    {p.popular && (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full text-[10px] font-bold text-white bg-gradient-to-l from-amber-500 to-orange-600 shadow-lg shadow-amber-500/30">
                        {t('landing.mostPopular')}
                      </div>
                    )}
                    <h3 className="font-bold text-base mb-1" style={{ fontFamily: 'Cairo, sans-serif' }}>{info.name}</h3>
                    <div className="mb-5 flex items-baseline gap-1.5">
                      <span className="text-3xl font-extrabold text-amber-400">{p.price === 0 ? l('مجاني', 'Free') : p.price}</span>
                      {p.price > 0 && <span className="text-sm font-semibold text-amber-400/60">{t('common.sar')}</span>}
                      {p.price > 0 && <span className="text-xs lp-text-30">/ {info.period}</span>}
                      {p.price === 0 && <span className="text-xs lp-text-40">• {info.period}</span>}
                    </div>
                    <div className="space-y-2.5 mb-6">
                      {info.features.map((f, j) => <div key={j} className="flex items-center gap-2.5 text-xs lp-text-50"><Check className="w-3.5 h-3.5 text-amber-400 flex-shrink-0" /> {f}</div>)}
                    </div>
                    <Link to="/login" className="block text-center py-3 rounded-xl text-xs font-bold transition-all duration-300 hover:scale-[1.02]" style={{ background: p.popular ? 'linear-gradient(135deg, #D97706, #F59E0B)' : 'rgba(var(--lp-rgb),0.06)', color: p.popular ? '#fff' : 'var(--lp-text)', border: p.popular ? 'none' : '1px solid rgba(var(--lp-rgb),0.1)', boxShadow: p.popular ? '0 4px 12px rgba(217,119,6,0.3)' : 'none' }}>
                      {p.price === 0 ? t('landing.startFree') : t('landing.planSubscribe')}
                    </Link>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* ====== TESTIMONIALS (Marquee) ====== */}
        <section className="py-16 md:py-24 overflow-hidden" style={{ background: 'rgba(var(--lp-rgb),0.01)' }}>
          <div className="max-w-6xl mx-auto px-4">
            <div data-anim="reveal" className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-extrabold mb-3" style={{ fontFamily: 'Cairo, sans-serif' }}>{t('landing.testimonialsTitle')}</h2>
              <p className="text-sm lp-text-40">{l('تجارب حقيقية من أصحاب مشاريع غذائية سعودية', 'Real experiences from Saudi food business owners')}</p>
            </div>
          </div>
          {/* Marquee container */}
          <div className="relative">
            <div className="absolute left-0 top-0 bottom-0 w-16 md:w-32 z-10" style={{ background: 'linear-gradient(to right, var(--lp-bg), transparent)' }} />
            <div className="absolute right-0 top-0 bottom-0 w-16 md:w-32 z-10" style={{ background: 'linear-gradient(to left, var(--lp-bg), transparent)' }} />
            <div className="flex gap-4" style={{ animation: `marqueeScroll ${isAr ? '25s' : '25s'} linear infinite`, width: 'max-content' }}>
              {[...TESTIMONIALS, ...TESTIMONIALS, ...TESTIMONIALS].map((tm, i) => {
                const info = tm[lang] || tm.en;
                return (
                  <article key={i} data-testid={i < TESTIMONIALS.length ? `testimonial-${i}` : undefined} className="flex-shrink-0 w-80 md:w-96 p-5 rounded-2xl" style={{ background: 'rgba(var(--lp-rgb),0.02)', border: '1px solid rgba(var(--lp-rgb),0.05)' }}>
                    <div className="flex gap-0.5 mb-3">{[1,2,3,4,5].map(s => <Star key={s} className="w-3.5 h-3.5 text-amber-400 fill-amber-400" />)}</div>
                    <p className="text-xs md:text-sm lp-text-50 leading-relaxed mb-4" style={{ minHeight: 48 }}>"{info.text}"</p>
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-gradient-to-br from-amber-500/20 to-orange-600/20 flex items-center justify-center text-xs font-bold text-amber-400">
                        {info.name.charAt(0)}
                      </div>
                      <div><div className="text-sm font-bold">{info.name}</div><div className="text-[11px] lp-text-30">{info.role}</div></div>
                    </div>
                  </article>
                );
              })}
            </div>
          </div>
        </section>

        {/* ====== FAQ ====== */}
        <section id="faq" className="py-16 md:py-24 px-4">
          <div className="max-w-2xl mx-auto">
            <div data-anim="reveal" className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-extrabold mb-3" style={{ fontFamily: 'Cairo, sans-serif' }}>{t('landing.faqTitle')}</h2>
            </div>
            <div data-anim="stagger-children" className="space-y-3">
              {FAQS.map((faq, i) => {
                const info = faq[lang] || faq.en;
                return (
                  <div key={i} className="rounded-xl overflow-hidden" style={{ background: 'rgba(var(--lp-rgb),0.02)', border: '1px solid rgba(var(--lp-rgb),0.05)' }}>
                    <button onClick={() => setOpenFaq(openFaq === i ? null : i)} className="w-full flex items-center justify-between p-4 text-start text-sm font-semibold hover:bg-white/[0.02] transition-colors" data-testid={`faq-${i}`}>
                      <span>{info.q}</span>
                      <ChevronDown className={`w-4 h-4 lp-text-30 transition-transform duration-300 flex-shrink-0 ${isAr ? 'mr-3' : 'ml-3'} ${openFaq === i ? 'rotate-180' : ''}`} />
                    </button>
                    {openFaq === i && <div className="px-4 pb-4 text-xs md:text-sm lp-text-40 leading-relaxed" style={{ animation: 'faqOpen 0.3s ease-out' }}>{info.a}</div>}
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* ====== FINAL CTA ====== */}
        <section className="py-16 md:py-24 px-4">
          <div data-anim="reveal" className="max-w-2xl mx-auto text-center p-8 md:p-12 rounded-3xl" style={{ background: 'linear-gradient(135deg, rgba(217,119,6,0.08), rgba(99,102,241,0.05))', border: '1px solid rgba(217,119,6,0.15)' }}>
            <Sparkles className="w-8 h-8 text-amber-400 mx-auto mb-4" />
            <h2 className="text-xl md:text-2xl font-extrabold mb-3" style={{ fontFamily: 'Cairo, sans-serif' }}>
              {l('ابدأ رحلتك مع ذوق AI اليوم', 'Start Your Journey with Zouq AI Today')}
            </h2>
            <p className="text-sm lp-text-40 mb-6">{l('14 يوم تجربة مجانية. بدون بطاقة ائتمان. إعداد في دقائق.', '14-day free trial. No credit card. Setup in minutes.')}</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/login" data-testid="cta-btn" className="inline-flex items-center justify-center gap-2 px-7 py-3 rounded-xl text-sm font-bold text-white bg-gradient-to-l from-amber-500 to-orange-600 shadow-lg shadow-amber-500/25">
                {l('ابدأ الآن مجاناً', 'Start Free Now')} <ArrowLeft className={`w-4 h-4 ${!isAr ? 'rotate-180' : ''}`} />
              </Link>
              <Link to="/demo" data-testid="final-demo-btn" className="inline-flex items-center justify-center gap-2 px-7 py-3 rounded-xl text-sm font-bold lp-text-70" style={{ background: 'rgba(var(--lp-rgb),0.06)', border: '1px solid rgba(var(--lp-rgb),0.1)' }}>
                <Play className="w-3.5 h-3.5" /> {l('عرض تجريبي', 'Live Demo')}
              </Link>
            </div>
          </div>
        </section>
      </main>

      {/* ====== FOOTER ====== */}
      <footer className="pt-16 pb-8 px-4 border-t" style={{ borderColor: 'rgba(var(--lp-rgb),0.05)', background: 'var(--lp-footer-bg)' }}>
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-10 mb-12">
            {/* Brand Column */}
            <div className="md:col-span-2">
              <div className="flex items-center gap-2.5 mb-4">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center shadow-lg shadow-amber-500/20">
                  <ChefHat className="w-5 h-5 text-white" />
                </div>
                <span className="font-extrabold text-xl" style={{ fontFamily: 'Cairo, sans-serif' }}>Zouq AI</span>
              </div>
              <p className="text-sm lp-text-40 leading-relaxed max-w-sm mb-6">
                {l('منصة ذكاء اصطناعي شاملة لإدارة المشاريع الغذائية في السعودية. مطاعم، كافيهات، وعربات متنقلة.', 'A comprehensive AI platform for managing food businesses in Saudi Arabia. Restaurants, cafés, and food trucks.')}
              </p>
              <div className="flex gap-3">
                {['X', 'In', 'ig'].map((s, i) => (
                  <div key={i} className="w-9 h-9 rounded-lg flex items-center justify-center text-xs font-bold lp-text-30 cursor-pointer transition-all hover:lp-text-60" style={{ background: 'rgba(var(--lp-rgb),0.05)', border: '1px solid rgba(var(--lp-rgb),0.08)' }}>
                    {s}
                  </div>
                ))}
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="font-bold text-sm mb-4" style={{ fontFamily: 'Cairo, sans-serif', color: '#F8FAFC' }}>
                {l('روابط سريعة', 'Quick Links')}
              </h4>
              <div className="space-y-2.5">
                {[
                  { id: 'features', label: t('landing.navFeatures') },
                  { id: 'pricing', label: t('landing.navPricing') },
                  { id: 'solutions', label: l('الحلول', 'Solutions') },
                  { id: 'faq', label: t('landing.navFaq') },
                ].map(link => (
                  <button key={link.id} onClick={() => scrollTo(link.id)} className="block text-sm lp-text-35 hover:text-amber-400 transition-colors" data-testid={`footer-link-${link.id}`}>
                    {link.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Product */}
            <div>
              <h4 className="font-bold text-sm mb-4" style={{ fontFamily: 'Cairo, sans-serif', color: '#F8FAFC' }}>
                {l('المنتج', 'Product')}
              </h4>
              <div className="space-y-2.5">
                {[
                  { to: '/restaurants', label: l('للمطاعم', 'For Restaurants') },
                  { to: '/cafes', label: l('للكافيهات', 'For Cafés') },
                  { to: '/food-trucks', label: l('للعربات المتنقلة', 'For Food Trucks') },
                  { to: '/login', label: l('تسجيل الدخول', 'Login') },
                  { to: '/demo', label: l('عرض تجريبي', 'Live Demo') },
                  { to: '/onboard', label: l('ابدأ الآن', 'Get Started') },
                ].map(link => (
                  <Link key={link.to} to={link.to} className="block text-sm lp-text-35 hover:text-amber-400 transition-colors" data-testid={`footer-product-${link.to.replace('/', '').replace('/', '-')}`}>
                    {link.label}
                  </Link>
                ))}
              </div>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="pt-6 flex flex-col md:flex-row items-center justify-between gap-3" style={{ borderTop: '1px solid rgba(var(--lp-rgb),0.06)' }}>
            <p className="text-xs lp-text-20">© 2026 Zouq AI. {t('landing.footerRights')}.</p>
            <div className="flex items-center gap-4">
              <Link to="/privacy" className="text-xs lp-text-25 hover:text-amber-400 transition-colors" data-testid="footer-privacy">
                {l('سياسة الخصوصية', 'Privacy Policy')}
              </Link>
              <Link to="/terms" className="text-xs lp-text-25 hover:text-amber-400 transition-colors" data-testid="footer-terms">
                {l('شروط الاستخدام', 'Terms of Service')}
              </Link>
            </div>
            <p className="text-xs lp-text-15">{l('صُنع بحب في السعودية 🇸🇦', 'Made with love in Saudi Arabia 🇸🇦')}</p>
          </div>
        </div>
      </footer>

      <ChatWidget />

      <style>{`
        @keyframes fadeSlideIn {
          from { opacity: 0; transform: translateY(12px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes marqueeScroll {
          0% { transform: translateX(0); }
          100% { transform: translateX(-33.33%); }
        }
        @keyframes faqOpen {
          from { opacity: 0; max-height: 0; padding-top: 0; padding-bottom: 0; }
          to { opacity: 1; max-height: 200px; padding-top: 0; padding-bottom: 16px; }
        }
        @keyframes growUp {
          from { transform: scaleY(0); }
          to { transform: scaleY(1); }
        }
        @keyframes shimmer {
          0% { background-position: 200% center; }
          100% { background-position: -200% center; }
        }
      `}</style>
    </div>
    </>
  );
}
